import { QueryClient } from '@tanstack/react-query';
import { isNetworkError } from '~shared/api/api-client';

/**
 * Retries a query once, and only when the request never got a response.
 *
 * A dropped connection on the first request (Safari's `Load failed`) otherwise
 * reaches the root error boundary straight away, although a second attempt
 * usually succeeds (samera/samera#3593). An HTTP error, a contract or a mapper
 * error still fails fast: repeating it would give the same answer. Mutations
 * keep their default of no retry — a repeated booking or payment call can book
 * twice.
 *
 * TanStack pauses a pending retry while the tab is hidden or the browser is
 * offline. A failure in a background tab therefore keeps the loading state up
 * until the guest returns, instead of showing the error page.
 */
export const retryNetworkErrorOnce = (
  failureCount: number,
  error: Error,
): boolean => failureCount < 1 && isNetworkError(error);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryNetworkErrorOnce,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 3,
    },
  },
});

declare module '@tanstack/react-query' {
  interface Register {
    // Deliberately the common Error supertype rather than ApiError: a query
    // function can also throw a MoneyContractError or a mapper error, not only
    // a transport one. Consumers read backend messages via
    // getApiErrorMessages(), which narrows.
    defaultError: Error;
  }
}
